import { apiClient, ENV } from './client';
import {
  User,
  Preferences,
  Therapist,
  Session,
  Credit,
  Payment,
  RegisterModel,
  ConfirmResponse,
  VerifyOtpModel,
  Fees,
  Ratings,
  InitPaymentResponse,
} from '../types';

// ── Customer / Auth ─────────────────────────────────────────────────────────

const encodePhone = (phone: string) => encodeURIComponent(phone);

const unwrapData = <T>(body: unknown): T => {
  if (body && typeof body === 'object' && 'data' in body) {
    return (body as { data: T }).data;
  }
  return body as T;
};

const isApiSuccess = (body: unknown): boolean => {
  if (body === true) return true;
  if (!body || typeof body !== 'object') return false;
  const b = body as Record<string, unknown>;
  if (b.status === 200 || b.status === '200' || b.status === 'success') return true;
  if (b.message === 'Success' || b.message === 'success') return true;
  if (b.verified === true || b.success === true) return true;
  const inner = b.data;
  if (inner === true) return true;
  if (inner && typeof inner === 'object') return true;
  return false;
};

const isUuid = (value: unknown): value is string =>
  typeof value === 'string' &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);

// const normalizeTherapist = (r: any): Therapist => ({
//   therapistId: r.therapist_id ?? r.therapistId ?? '',
//   name:        r.name ?? `${r.first_name ?? ''} ${r.last_name ?? ''}`.trim(),
//   firstName:   r.first_name ?? r.firstName ?? '',
//   lastName:    r.last_name  ?? r.lastName  ?? '',
//   // email:       r.email ?? '',
//   // phoneNumber: r.primary_phone_no ?? r.phoneNumber ?? '',
//   country:     r.country ?? '',
//   status:      r.status ?? '',
//   expertise:   r.expertise ?? r.areaofexpertise ?? '',
//   organization:r.organization ?? '',
//   rate:        r.rate ?? r.sessionRate ?? 0,
//   url:         r.url ?? r.profileImage ?? '',
//   dateCreated: r.date_created ?? r.dateCreated ?? '',
// });
const normalizeTherapist = (r: any): Therapist => ({
  therapistId: r.therapist_id ?? r.therapistId ?? '',
  name:
    r.name ??
    `${r.first_name ?? r.firstName ?? ''} ${r.last_name ?? r.lastName ?? ''}`.trim(),
  firstName: r.first_name ?? r.firstName ?? '',
  lastName: r.last_name ?? r.lastName ?? '',
  country: r.country ?? '',
  status: r.status ?? '',
  expertise: r.expertise ?? r.areaofexpertise ?? '',
  organization: r.organization ?? '',
  rate: r.rate ?? r.sessionRate ?? r.credits ?? 0,
  url:
    r.profile_picture_url ??
    r.profileImage ??
    r.profile_picture ??
    r.url ??
    '',
  dateCreated: r.date_created ?? r.dateCreated ?? '',
});
const normalizeCustomer = (raw: Record<string, unknown>): User => {
  const id = [raw.id, raw.customer_id, raw.customerId, raw.uuid].find(isUuid);

  return {
  id,
  firstName: (raw.firstName as string) || '',
  lastName: (raw.lastName as string) || '',
  email: raw.email as string | undefined,
  phoneNumber: (raw.phoneNumber as string) || (raw.primaryPhoneNo as string),
  country: raw.country as string | undefined,
  nationalId: raw.nationalId as string | undefined,
  postalAddress: raw.postalAddress as string | undefined,
  tin: raw.tin as string | undefined,
  message: raw.message as string | undefined,
  };
};

/** GET /customer/login?phone=... */
export const therappLogin = async (phone: string): Promise<User> => {
  const res = await apiClient.get(ENV.CUSTOMER_LOGIN, { params: { phone } });
  const payload = unwrapData<Record<string, unknown>>(res.data);
  return normalizeCustomer(payload || {});
};

/** GET /customer/registration/data-lake-login/{phone} */
export const dataLakeLogin = async (phone: string): Promise<User> => {
  const res = await apiClient.get(`${ENV.CUSTOMER_DATA_LAKE_LOGIN}${encodePhone(phone)}`);
  return normalizeCustomer(unwrapData<Record<string, unknown>>(res.data) || {});
};

/** GET /customer/registration/alternate-login/{phone} */
export const alternateLogin = async (phone: string): Promise<User> => {
  const res = await apiClient.get(`${ENV.CUSTOMER_ALTERNATE_LOGIN}${encodePhone(phone)}`);
  return normalizeCustomer(unwrapData<Record<string, unknown>>(res.data) || {});
};

/** GET /customer/send-otp/{phone} */
export const sendOtp = async (phoneNumber: string): Promise<string> => {
  const res = await apiClient.get(`${ENV.CUSTOMER_SEND_OTP}${encodePhone(phoneNumber)}`);
  return res.data?.message || (typeof res.data === 'string' ? res.data : '');
};

/** POST /customer/verify */
export const verifyOtp = async (data: VerifyOtpModel): Promise<boolean> => {
  const res = await apiClient.post(ENV.CUSTOMER_VERIFY, {
    otp: data.code,
    code: data.code,
    phone: data.phoneNumber,
    phoneNumber: data.phoneNumber,
    primaryPhoneNo: data.phoneNumber,
  });
  return isApiSuccess(res.data);
};

/** POST /customer/registration/create */
export const registerUser = async (data: RegisterModel): Promise<User> => {
  const res = await apiClient.post(ENV.CUSTOMER_REGISTER, data);
  return normalizeCustomer(unwrapData<Record<string, unknown>>(res.data) || {});
};

/** GET /customer/fetch-all */
export const fetchAllCustomers = async (): Promise<User[]> => {
  const res = await apiClient.get(ENV.CUSTOMER_FETCH_ALL);
  return res.data || [];
};

/** GET /customer/{id}/details */
export const fetchCustomerDetails = async (id: string): Promise<User> => {
  const res = await apiClient.get(`${ENV.CUSTOMER_DETAILS}${id}/details`);
  return normalizeCustomer(unwrapData<Record<string, unknown>>(res.data) || {});
};

/** PUT /customer/{id}/update */
export const updateCustomer = async (id: string, data: Partial<User>): Promise<User> => {
  const res = await apiClient.put(`${ENV.CUSTOMER_UPDATE}${id}/update`, data);
  return normalizeCustomer(unwrapData<Record<string, unknown>>(res.data) || {});
};

export const saveUserInfo = async (data: User): Promise<string> => {
  if (!data.id) throw new Error('Customer id is required to update profile.');
  const res = await updateCustomer(data.id, data);
  return (res as User & { message?: string })?.message || 'Success';
};

export const confirmUser = async (data: any, userId: string): Promise<ConfirmResponse> => {
  const res = await apiClient.post(`${ENV.CONFIRM_USER}${userId}`, data);
  return res.data;
};

/** PATCH /customer/delete-account */
export const deleteAccount = async (data: {
  userId?: string;
  customerId?: string;
  reason?: string;
}): Promise<any> => {
  const res = await apiClient.patch(ENV.CUSTOMER_DELETE, data);
  return res.data;
};

// ── Preferences ─────────────────────────────────────────────────────────────

/** POST /preferences */
export const savePreferences = async (data: Preferences): Promise<Preferences> => {
  const res = await apiClient.post(ENV.SAVE_PREFERENCE, data);
  const raw = unwrapData<Record<string, unknown>>(res.data);
  return normalizePreferences(raw || {});
};

export const editPreferences = async (data: Preferences): Promise<Preferences> => {
  const res = await apiClient.put(`${ENV.EDIT_PREFERENCE}${data.id}`, data);
  const raw = unwrapData<Record<string, unknown>>(res.data);
  return normalizePreferences(raw || {});
};
export const checkPreferences = async (userId: string): Promise<boolean> => {
  const res = await apiClient.get(ENV.CHECK_PREFERENCE, { params: { userId } }); // ?userId=
  return isApiSuccess(res.data);
};
/** PUT /preferences/{id} */


const normalizePreferences = (raw: Record<string, unknown>): Preferences => ({
  id:                  raw.id as string,
  userId:              (raw.userid ?? raw.userId) as string,
  userType:            (raw.usertype ?? raw.userType) as string,
  gender:              raw.gender as string || '',
  ageGroup:            (raw.agegroup ?? raw.ageGroup) as string || '',
  nickName:            (raw.nickname ?? raw.nickName) as string || '',
  scheduleFor:         (raw.schedulefor ?? raw.scheduleFor) as string || '',
  faithInclination:    (raw.faithinclination ?? raw.faithInclination) as string || '',
  type:                (raw.type as string[]) || [],
  expertise:           (raw.areaofexpertise ?? raw.expertise) as string[] || [],
  shareFeedback:       (raw.sharefeedback ?? raw.shareFeedback) as boolean || false,
  reminders:           raw.reminders as boolean ?? true,
  accessibilityOption: (raw.accessibilityoption ?? raw.accessibilityOption) as boolean || false,
});

// Preferences
export const fetchPreferences = async (userId: string): Promise<Preferences | null> => {
  const res = await apiClient.get(ENV.FETCH_PREFERENCES, { params: { userId } }); // ?userId=
  const data = unwrapData<Record<string, unknown> | Record<string, unknown>[]>(res.data);
  if (!data) return null;
  const raw = Array.isArray(data) ? data[0] : data;
  if (!raw?.id) return null;
  return normalizePreferences(raw);
};

// ── Therapists ────────────────────────────────────────────────

// /** GET /preferences/matching-therapists */
// export const fetchMatchingTherapists = async (): Promise<Therapist[]> => {
//   const res = await apiClient.get(ENV.FETCH_MATCHING_THERAPISTS, {
//     params: { userType: 'Customer' },
//   });
//   return unwrapData<Therapist[]>(res.data) || [];
// };

/** GET /preferences/matching-therapists/for-customer/{customerId} */
export const fetchMatchingTherapists = async (
  customerId: string
): Promise<Therapist[]> => {
  const res = await apiClient.get(
    `${ENV.FETCH_MATCHING_THERAPISTS}/for-customer/${customerId}`
  );

  const data = unwrapData<unknown>(res.data);

  if (!Array.isArray(data)) return [];

  return data.map(normalizeTherapist);
};
/** GET /therapist/get-all-therapists */
export const fetchTherapists = async (): Promise<Therapist[]> => {
  const res = await apiClient.get(ENV.FETCH_THERAPISTS);
  const unwrapped = unwrapData<Therapist[]>(res.data);
  return Array.isArray(unwrapped) ? unwrapped : [];
};
/** GET /therapist/get-random-therapists */
// export const fetchRandomTherapists = async (): Promise<Therapist[]> => {
//   const res = await apiClient.get(ENV.FETCH_RANDOM_THERAPISTS);
//   const body = res.data;
//   // this endpoint wraps in "response", not "data"
//   const raw: unknown[] = Array.isArray(body?.response)
//     ? body.response
//     : Array.isArray(body?.data)
//     ? body.data
//     : Array.isArray(body)
//     ? body
//     : [];
//   return raw.map(normalizeTherapist);
// };
export const fetchRandomTherapists = async (): Promise<Therapist[]> => {
  const res = await apiClient.get(ENV.FETCH_RANDOM_THERAPISTS);

  const body = res.data;

  const raw: unknown[] =
    Array.isArray(body?.response)
      ? body.response
      : Array.isArray(body?.data)
        ? body.data
        : Array.isArray(body)
          ? body
          : [];

  return raw.map(normalizeTherapist);
};

/** GET /therapist/get-by-id/{therapist_id} */
export const fetchTherapistById = async (id: string): Promise<Therapist> => {
  const res = await apiClient.get(`${ENV.THERAPIST_BASE}${id}`);
  return unwrapData<Therapist>(res.data);
};

// ── Sessions ─────────────────────────────────────────────────────────────────

/** GET /sessions/customers/{customerId} */
export const fetchSessionsByCustomer = async (customerId: string): Promise<Session[]> => {
  const res = await apiClient.get(`${ENV.FETCH_SESSIONS_BY_CUSTOMER}${customerId}`);
  return unwrapData<Session[]>(res.data) || [];
};

/** POST /sessions/book */
export const fetchSessions = async (): Promise<Session[]> => {
  const res = await apiClient.get(ENV.FETCH_SESSIONS);
  return unwrapData<Session[]>(res.data) || [];
};
// export const bookSession = async (data: Session): Promise<Session> => {
//   const res = await apiClient.post(ENV.BOOK_SESSION, data);
//   return unwrapData<Session>(res.data);
// };
export interface BookSessionResponse {
  status: number;
  error: boolean;
  message: string;
  data: Session | null;
}

export const bookSession = async (
  data: Session
): Promise<BookSessionResponse> => {
  const res = await apiClient.post(ENV.BOOK_SESSION, data);

  return res.data;
};
/** PUT /sessions/cancel */
export const cancelSession = async (sessionId: string, reason: string): Promise<any> => {
  const res = await apiClient.put(ENV.CANCEL_SESSION, { sessionId, reason });
  return res.data;
};

/** GET /sessions/therapists/{therapistId} */
export const fetchTherapistSessions = async (therapistId: string): Promise<Session[]> => {
  const res = await apiClient.get(`${ENV.FETCH_THERAPIST_SESSIONS}${therapistId}`);
  return unwrapData<Session[]>(res.data) || [];
};

/** GET /sessions/fetch-by-status/{status} */
export const fetchSessionsByStatus = async (status: string): Promise<Session[]> => {
  const res = await apiClient.get(`${ENV.FETCH_SESSIONS_BY_STATUS}${status}`);
  return unwrapData<Session[]>(res.data) || [];
};

/** GET /sessions/booking/{startDate}/{endDate} */
export const fetchSessionsByDateRange = async (startDate: string, endDate: string): Promise<Session[]> => {
  const res = await apiClient.get(`${ENV.FETCH_SESSIONS_BY_DATE}${startDate}/${endDate}`);
  return unwrapData<Session[]>(res.data) || [];
};

/** GET /sessions/{id}/details */
export const fetchSessionDetails = async (id: string): Promise<Session> => {
  const res = await apiClient.get(`${ENV.SESSION_BASE}${id}/details`);
  return unwrapData<Session>(res.data);
};

/** PATCH /sessions/{sessionID}/complete */
export const completeSession = async (sessionId: string): Promise<any> => {
  const res = await apiClient.patch(`${ENV.SESSION_BASE}${sessionId}/complete`);
  return res.data;
};

/** PATCH /sessions/report */
export const addSessionReport = async (data: { sessionId: string; report: string }): Promise<any> => {
  const res = await apiClient.patch(ENV.SESSION_REPORT, data);
  return res.data;
};

// ── Credits ───────────────────────────────────────────────────────────────────

/** GET /session-credits/customer/{customerID} */
export const getSessionCredit = async (customerId: string): Promise<Credit> => {
  if (!isUuid(customerId)) {
    throw new Error('A valid customer UUID is required to fetch session credits.');
  }
  const res = await apiClient.get(`${ENV.FETCH_CREDITS}${customerId}`);
  const data = unwrapData<Credit | Credit[]>(res.data);
  return (Array.isArray(data) ? data[0] : data) || { quantity: 0 };
};

/** POST /session-credits/create */
export const createCredit = async (data: any): Promise<Credit> => {
  const res = await apiClient.post(ENV.CREATE_CREDIT, data);
  return unwrapData<Credit>(res.data);
};

/** PUT /session-credits/apply */
export const applyCredit = async (data: any): Promise<any> => {
  const res = await apiClient.put(ENV.APPLY_CREDIT, data);
  return res.data;
};

/** PUT /session-credits/refund */
export const refundCredit = async (data: any): Promise<any> => {
  const res = await apiClient.put(ENV.REFUND_CREDIT, data);
  return res.data;
};

// ── Payments ──────────────────────────────────────────────────────────────────

/** POST /payments/initiate */
export const initiatePayment = async (data: any): Promise<InitPaymentResponse> => {
  const res = await apiClient.post(ENV.INITIATE_PAYMENT, data); // POST /payments
  const paymentResponse = res.data?.response;
  const payment = paymentResponse?.payment;

  return {
    checkoutURL: paymentResponse?.checkoutUrl || payment?.checkout_url || '',
    reference: payment?.client_reference,
    transactionId: payment?.payment_id,
  };
};
/** GET /payments/status/{payment_id} */
export const checkPaymentStatus = async (paymentId: string): Promise<any> => {
  const res = await apiClient.get(`${ENV.CHECK_PAYMENT_STATUS}${paymentId}`); // GET /payments/{payment_id}
  return res.data;
};

/** POST /payments/status/sync */
export const syncPaymentStatus = async (data: any): Promise<any> => {
  const res = await apiClient.post(ENV.SYNC_PAYMENT_STATUS, data);
  return res.data;
};

/** GET /payments/customer/{customer_id} */
export const getUserPayments = async (customerId: string): Promise<Payment[]> => {
  const res = await apiClient.get('/payments', { params: { customerId } });
  const data = res.data;

  // handle various response shapes
  const payments: Array<Record<string, any>> = Array.isArray(data)
    ? data
    : Array.isArray(data?.data)
      ? data.data
      : Array.isArray(data?.payments)
        ? data.payments
        : Array.isArray(data?.response)
          ? data.response
          : [];

  return payments.map((payment): Payment => ({
    id: payment.payment_id ?? payment.id,
    amount: Number(payment.payment_amount ?? payment.bill_amount ?? payment.amount ?? 0),
    currency: payment.currency ?? 'GHS',
    status: payment.status ?? 'PENDING',
    reference: payment.client_reference ?? payment.checkout_id ?? payment.reference,
    createdAt: payment.date_created ?? payment.createdAt,
  }));
};
/** GET /payments/session/{session_id} */
export const getSessionPayment = async (sessionId: string): Promise<Payment> => {
  const res = await apiClient.get(`${ENV.SESSION_PAYMENT}${sessionId}`);
  return unwrapData<Payment>(res.data);
};

/** POST /payments/credits/convert */
export const convertPaymentToCredits = async (data: any): Promise<any> => {
  const res = await apiClient.post(ENV.CONVERT_CREDITS, data);
  return res.data;
};

// ── Ratings ───────────────────────────────────────────────────────────────────

export const rateTherapist = async (data: Ratings): Promise<any> => {
  const res = await apiClient.post(ENV.RATINGS_BASE, data);
  return res.data;
};

export const fetchFees = async (): Promise<Fees> => {
  const res = await apiClient.get(ENV.FETCH_FEES);
  return unwrapData<Fees>(res.data);
};
